'use client';

import { ReactNode, useEffect, useRef } from 'react';
import classNames from 'classnames';
import styles from './Modal.module.scss';
import XCloseIcon from './icons/XCloseIcon';
import UIButton from './buttons/UIButton';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  titleId: string;
  children: ReactNode;
  className?: string;
}

const Modal = ({
  isOpen,
  onClose,
  titleId,
  children,
  className,
}: ModalProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen && !dialog.open) {
      dialog.showModal();
    } else if (!isOpen && dialog.open) {
      dialog.close();
    }
  }, [isOpen]);

  // Backdrop click closes modal via DOM listener
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleClick = (e: MouseEvent) => {
      if (e.target === dialog) {
        onClose();
      }
    };

    dialog.addEventListener('click', handleClick);
    return () => dialog.removeEventListener('click', handleClick);
  }, [onClose]);

  return (
    <dialog
      ref={dialogRef}
      className={classNames(styles.modal, className)}
      aria-labelledby={titleId}
      onClose={onClose}
    >
      <div className={styles.colorbar} />
      <div className={styles.modalContent}>
        <UIButton
          buttonType="tonal"
          label=""
          icon={<XCloseIcon />}
          onClick={onClose}
          aria-label="Close modal"
          className={styles.closeBtn}
        />
        {children}
      </div>
    </dialog>
  );
};

export default Modal;
